import React, { useState } from "react";
import { useLanguage } from "../context/LanguageContext";

// Simple lookup: (pathology, discipline) -> estimated recovery weeks
// Values are average return-to-sport estimates based on Dr Aswad's clinical protocols.
const MATRIX = {
  "coiffe": { base: 20, contact: 4, precision: -2 },
  "latarjet": { base: 16, contact: 4, precision: 0 },
  "epicondylite": { base: 12, contact: 2, precision: 0 },
  "canal-carpien": { base: 6, contact: 2, precision: -1 },
  "prothese": { base: 24, contact: 8, precision: -4 },
  "fracture-poignet": { base: 10, contact: 2, precision: -1 },
  "luxation-acromio": { base: 10, contact: 4, precision: -1 },
};

const CONTACT_SPORTS = ["rugby", "judo", "mma", "hockey", "boxe", "handball", "basketball"];
const PRECISION_SPORTS = ["golf", "tir", "fleshette", "chess"];

const RecoveryCalculator = () => {
  const { lang } = useLanguage();
  const [pathology, setPathology] = useState("coiffe");
  const [sport, setSport] = useState("tennis");
  const [age, setAge] = useState(35);
  const [result, setResult] = useState(null);

  const pathologies = lang === "fr" ? [
    { value: "coiffe", label: "Rupture de coiffe" },
    { value: "latarjet", label: "Instabilité épaule (Latarjet)" },
    { value: "epicondylite", label: "Tennis-elbow (arthroscopie)" },
    { value: "canal-carpien", label: "Canal carpien" },
    { value: "prothese", label: "Prothèse d'épaule" },
    { value: "fracture-poignet", label: "Fracture du poignet" },
    { value: "luxation-acromio", label: "Luxation acromio-claviculaire" },
  ] : [
    { value: "coiffe", label: "Rotator cuff tear" },
    { value: "latarjet", label: "Shoulder instability (Latarjet)" },
    { value: "epicondylite", label: "Tennis elbow (arthroscopy)" },
    { value: "canal-carpien", label: "Carpal tunnel" },
    { value: "prothese", label: "Shoulder prosthesis" },
    { value: "fracture-poignet", label: "Wrist fracture" },
    { value: "luxation-acromio", label: "Acromio-clavicular dislocation" },
  ];

  const sports = ["rugby", "tennis", "natation", "handball", "golf", "judo", "moto", "crossfit", "volley", "escalade", "basketball", "running"];

  const compute = (e) => {
    e.preventDefault();
    const m = MATRIX[pathology];
    if (!m) return;
    let weeks = m.base;
    const s = sport.toLowerCase();
    if (CONTACT_SPORTS.includes(s)) weeks += m.contact;
    if (PRECISION_SPORTS.includes(s)) weeks += m.precision;
    if (age >= 55) weeks += 2;
    if (age >= 65) weeks += 3;
    if (age < 25) weeks -= 1;
    weeks = Math.max(4, weeks);
    setResult(weeks);
  };

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="section-padding bg-[#F8FAFC]"
    >
      <div className="container-page">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="chip mb-5">
              {lang === "fr" ? "Outil interactif" : "Interactive tool"}
            </span>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-navy leading-[1.05]">
              {lang === "fr"
                ? "Estimez votre délai de reprise sportive"
                : "Estimate your return-to-sport time"}
            </h2>
            <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
              {lang === "fr"
                ? "Simulateur basé sur les protocoles cliniques du Dr Aswad. Sélectionnez votre pathologie, votre sport principal et votre tranche d'âge."
                : "Simulator based on Dr Aswad's clinical protocols. Select your pathology, main sport and age."}
            </p>
            <p className="mt-4 text-xs text-muted-foreground italic">
              {lang === "fr"
                ? "⚠️ Ce résultat est une estimation, pas un diagnostic. Seule une consultation permet d'établir un plan personnalisé."
                : "⚠️ This is an estimate, not a diagnosis. Only a consultation provides a personalized plan."}
            </p>
          </div>

          <form
            onSubmit={compute}
            className="lg:col-span-7 rounded-2xl bg-white border border-border p-6 md:p-8 shadow-sm"
            data-testid="calculator-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                  {lang === "fr" ? "Pathologie" : "Pathology"}
                </label>
                <select
                  value={pathology}
                  onChange={(e) => setPathology(e.target.value)}
                  data-testid="calc-pathology"
                  className="mt-2 w-full rounded-md border border-border bg-white px-4 py-3 text-sm font-semibold focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20"
                >
                  {pathologies.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                  {lang === "fr" ? "Sport principal" : "Main sport"}
                </label>
                <select
                  value={sport}
                  onChange={(e) => setSport(e.target.value)}
                  data-testid="calc-sport"
                  className="mt-2 w-full rounded-md border border-border bg-white px-4 py-3 text-sm font-semibold focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20 capitalize"
                >
                  {sports.map((s) => (
                    <option key={s} value={s} className="capitalize">{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.14em] text-foreground/70">
                  {lang === "fr" ? "Âge" : "Age"} : <span className="text-[#FF5A1F]">{age}</span>
                </label>
                <input
                  type="range"
                  min="16"
                  max="85"
                  value={age}
                  onChange={(e) => setAge(Number(e.target.value))}
                  data-testid="calc-age"
                  className="mt-4 w-full accent-[#FF5A1F]"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="calc-submit"
              className="mt-6 btn-primary w-full"
            >
              <i className="fa-solid fa-calculator" aria-hidden="true"></i>
              {lang === "fr" ? "Calculer mon délai" : "Calculate my timeline"}
            </button>

            {result !== null && (
              <div
                data-testid="calc-result"
                className="mt-6 rounded-xl bg-[#0A192F] text-white p-6 animate-fade-in"
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">
                  {lang === "fr" ? "Estimation de reprise" : "Estimated return"}
                </p>
                <p className="mt-2 font-display text-4xl sm:text-5xl font-black">
                  {result} <span className="text-xl text-white/70">
                    {lang === "fr" ? "semaines" : "weeks"}
                  </span>
                </p>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">
                  {lang === "fr"
                    ? `Soit environ ${Math.round(result / 4.33)} mois. Cette estimation suppose une chirurgie sans complication et une bonne compliance à la rééducation.`
                    : `About ${Math.round(result / 4.33)} months. This estimate assumes uncomplicated surgery and good rehab compliance.`}
                </p>
                <a
                  href="#contact"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#FF5A1F] hover:text-white transition"
                >
                  {lang === "fr" ? "Demander un avis personnalisé" : "Request a personalized opinion"}
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </a>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default RecoveryCalculator;
